import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const formatNum = (n) => {
  if (!n && n !== 0) return '0';
  return Math.round(n)?.toLocaleString('fr-FR')?.replace(/,/g, ' ');
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload?.[0]?.payload;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs"
      style={{ backgroundColor: '#1f1f1f', border: '1px solid rgba(229,32,42,0.4)', fontFamily: 'Inter, sans-serif' }}
    >
      <p className="font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.9)' }}>{d?.username}</p>
      <p style={{ color: '#f59e0b' }}>{formatNum(d?.diamonds)}</p>
      <p style={{ color: 'rgba(255,255,255,0.4)' }}>{d?.server}</p>
    </div>
  );
};

const SkeletonChart = () => (
  <div
    className="rounded-xl p-5 animate-pulse"
    style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f', height: '320px' }}
  >
    <div className="h-3 w-32 rounded mb-6" style={{ backgroundColor: '#1f1f1f' }} />
    {Array.from({ length: 10 })?.map((_, i) => (
      <div key={i} className="flex items-center gap-3 mb-3">
        <div className="h-2 w-20 rounded" style={{ backgroundColor: '#1f1f1f' }} />
        <div className="h-5 rounded" style={{ backgroundColor: '#1f1f1f', width: `${30 + (i * 5)}%` }} />
      </div>
    ))}
  </div>
);

const Top10Chart = ({ data, loading, serversData }) => {
  const { t } = useTranslation();

  const top10 = useMemo(() => {
    if (!data?.length) return [];
    return [...data]
      ?.sort((a, b) => (parseInt(b?.diamonds) || 0) - (parseInt(a?.diamonds) || 0))
      ?.slice(0, 10)
      ?.map(r => ({
        username: r?.username || 'Unknown',
        diamonds: parseInt(r?.diamonds) || 0,
        server: r?.server || '',
      }))
      ?.reverse();
  }, [data]);

  const getServerColor = (serverId) => {
    const s = serversData?.find(sv => sv?.id === serverId);
    return s?.color || '#E5202A';
  };

  if (loading) return <SkeletonChart />;

  if (!top10?.length) {
    return (
      <div
        className="rounded-xl p-5 flex items-center justify-center"
        style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f', height: '280px' }}
      >
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performance.noDataAvailable', 'Aucune donnée disponible')}
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl p-5"
      style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f' }}
    >
      <ResponsiveContainer width="100%" height={320}>
        <BarChart
          data={top10}
          layout="vertical"
          margin={{ top: 0, right: 30, left: 10, bottom: 0 }}
        >
          <CartesianGrid horizontal={false} stroke="#1f1f1f" />
          <XAxis
            type="number"
            tickFormatter={v => formatNum(v)}
            tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
            axisLine={{ stroke: '#1f1f1f' }}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="username"
            width={110}
            tick={{ fill: 'rgba(255,255,255,0.65)', fontSize: 11, fontFamily: 'Inter, sans-serif' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
          <Bar dataKey="diamonds" radius={[0, 4, 4, 0]} maxBarSize={22}>
            {top10?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getServerColor(entry?.server)}
                fillOpacity={0.85}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Top10Chart;
